import { WorkflowTemplate } from '@amzn/finspace-service-typescript-client';
import { createStore } from './store-factory';
import { useWorkflowStore } from './workflow-store';

interface AuthState {
  isInitialized: boolean;
  isInitializing: boolean;
  authorizedWorkflowTypes: string[];
  error: any;
  initializeAuthorization: () => Promise<void>;
  refreshAuthorizationFromTemplates: (templates: WorkflowTemplate[]) => void;
  hasAccess: (workflowType: string) => boolean;
  getAuthorizedWorkflowTypes: () => string[];
}

/**
 * Extract workflow types from templates based on tags.
 * The backend returns only templates the user is authorized to access,
 * so we can derive authorized workflow types from the returned templates.
 */
const extractWorkflowTypesFromTemplates = (templates: WorkflowTemplate[]): string[] => {
  const workflowTypes = new Set<string>();
  
  templates.forEach(template => {
    if (template.tags && Array.isArray(template.tags)) {
      template.tags.forEach(tag => {
        // Tags like 'MFR', 'PPM', 'RVM' represent workflow types
        workflowTypes.add(tag);
      });
    }
  });
  
  return Array.from(workflowTypes);
};

export const useAuthStore = createStore<AuthState>('auth', (set, get) => ({
  isInitialized: false,
  isInitializing: false,
  authorizedWorkflowTypes: [],
  error: null,

  /**
   * Initialize authorization by fetching workflow templates.
   * The backend performs Brass authorization and returns only templates
   * the user is authorized to access.
   */
  initializeAuthorization: async () => {
    const { isInitialized, isInitializing } = get();
    
    // Avoid duplicate initialization while already in progress
    if (isInitializing) {
      return;
    }
    
    // If already initialized, don't re-initialize - use cached authorization
    if (isInitialized) {
      return;
    }
    
    set({ isInitializing: true, error: null });
    
    try {
      // Use the workflow store to fetch templates
      const workflowStore = useWorkflowStore.getState();
      await workflowStore.fetchWorkflowTemplates();
      
      // Get the templates that were fetched - need to get fresh state after async call
      const updatedWorkflowStore = useWorkflowStore.getState();
      const templates = updatedWorkflowStore.workflowTemplates;
      
      // Extract authorized workflow types from templates
      const authorizedWorkflowTypes = extractWorkflowTypesFromTemplates(templates);
      
      set({ 
        authorizedWorkflowTypes, 
        isInitialized: true, 
        isInitializing: false 
      });
    } catch (error) {
      set({ 
        error, 
        isInitialized: true, // Mark as initialized even on error to prevent retry loops
        isInitializing: false,
        authorizedWorkflowTypes: [] // No access on error (fail closed)
      });
    }
  },

  /**
   * Refresh authorization from provided workflow templates.
   * Updates auth state before templates are stored in workflow store.
   */
  refreshAuthorizationFromTemplates: (templates: WorkflowTemplate[]) => {
    const authorizedWorkflowTypes = extractWorkflowTypesFromTemplates(templates);
    set({ authorizedWorkflowTypes, isInitialized: true });
  },

  /**
   * Check if the user has access to a specific workflow type.
   * Returns true if the workflow type is in the list of authorized types.
   */
  hasAccess: (workflowType: string): boolean => {
    const { authorizedWorkflowTypes, isInitialized } = get();
    
    // If not initialized, deny access (fail closed)
    if (!isInitialized) {
      return false;
    }
    
    return authorizedWorkflowTypes.includes(workflowType);
  },

  /**
   * Get the list of all authorized workflow types.
   */
  getAuthorizedWorkflowTypes: (): string[] => {
    return get().authorizedWorkflowTypes;
  },
}));
